import { getProductIdFromRoute } from '@/helpers/productRouteMapping';
import Footer from '@/layout/Footer';
import CurrencyConverter from '@/layout/Header/Currency';
import RightContent from '@/layout/Header/RightContent';
import Product from '@/services/Catalog/Product';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { FormattedMessage } from '@umijs/max';
import 'ag-grid-enterprise';
import { LicenseManager } from 'ag-grid-enterprise';
import { Button, Result, message } from 'antd';
import { Location } from 'history';
import React, { ReactNode, useEffect, useState } from 'react';
import defaultSettings from '../config/defaultSettings';
import ErrorBoundary from './ErrorBoundary';
import AuthProvider from './auth';
import useAggressiveClickDetector from './hooks/useAggressiveClickDetector';
import EndingPoliciesNotifier from './layout/EndingPoliciesNotifier';
import CheckBalance from './layout/Header/CheckBalance';
import NoticeBar from './layout/Header/NoticeBar';
import NoAccess from './pages/403';
import EndingPoliciesNotification from './services/EndingPoliciesNotification';
import FiscalizationNotification from './services/FiscalizationNotification';
import Logger from './services/Logger';
import { IInitialState } from './types/base';

LicenseManager.setLicenseKey('For_Trialing_ag-Grid_Only-Not_For_Real_Development_Or_Production_Projects-Valid_Until-21_April_2022_[v2]_MTY1MDQ5NTYwMDAwMA==f734ac0864b66e282a092463d38d2ef3');

export function rootContainer(container: ReactNode[]) {
    return React.createElement(AuthProvider, null, container);
}

export async function getInitialState(): Promise<{
    settings?: Partial<LayoutSettings>;
    name?: string;
    endingPoliciesCount?: number;
}> {
    return {
        settings: defaultSettings,
        name: '',
        endingPoliciesCount: EndingPoliciesNotification.getCachedCount(),
    };
}

const formatVersion = (numStringified: string) => {
    const num = Number(numStringified);

    if (Number.isNaN(num)) return null;

    const major = Math.floor(num / 100);

    const minor = Math.floor((num % 100) / 10);

    const patch = num % 10;

    return `v${major}.${minor}.${patch}`;
};

export const layout = ({ initialState, setInitialState }: IInitialState) => {
    return {
        headerContentRender: () => {
            return (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <CurrencyConverter />
                    <CheckBalance />
                </div>
            );
        },
        rightContentRender: () => <RightContent />,
        disableContentMargin: false,
        footerRender: () => <Footer />,
        menuFooterRender: (props: any) => {
            return (
                <div style={{ position: 'absolute', bottom: 35, left: '50%', transform: 'translateX(-50%)', textAlign: 'center', fontSize: 14, fontWeight: 600 }}>
                    {formatVersion(localStorage.getItem('githubActionsCount') || '')}
                </div>
            );
        },
        onPageChange: async (location: Location) => {
            // Destroy all modals
            message.destroy();

            // this callback is called 2 times when opening app with base url, this check is important to not trigger 2 times fiscalization notication
            if (location.pathname === '/' && location.key === 'default') return;

            await FiscalizationNotification.init();

            window.dispatchEvent(new Event(EndingPoliciesNotification.pageChangeEvent));
        },
        layoutBgImgList: [
            {
                src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
                left: 85,
                bottom: 100,
                height: '303px',
            },
            {
                src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
                bottom: -68,
                right: -45,
                height: '303px',
            },
            {
                src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
                bottom: 0,
                left: 0,
                width: '331px',
            },
        ],
        menuRender: (_props: any, defaultDom: ReactNode) => {
            return <div className="sidebar">{defaultDom}</div>;
        },
        menuTextRender: (item: { path?: string }, defaultDom: ReactNode) => {
            if (item.path !== '/policy/renewals') return defaultDom;

            const count = initialState?.endingPoliciesCount || 0;

            if (!count) return defaultDom;

            return (
                <span className="ending-policies-menu-item">
                    {defaultDom}
                    <div
                        style={{
                            background: '#ff6f75',
                            color: '#fff',
                            minWidth: 22,
                            height: 22,
                            fontWeight: 600,
                            borderRadius: 11,
                            fontSize: 11,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            paddingInline: '6px',
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        {count}
                    </div>
                </span>
            );
        },
        pageTitleRender: (_: any, staticTitle: string) => {
            const [pageTitle, setPageTitle] = useState<string>('');

            useEffect(() => {
                getProductName();
            }, [staticTitle]);

            const getProductName = async () => {
                // Dynamically set document title based on product page
                const productId = getProductIdFromRoute(location.pathname);

                if (!productId) {
                    setPageTitle(staticTitle);

                    document.title = staticTitle;

                    return;
                }

                try {
                    const response = await Product.get(productId);

                    if (response.data?.name) {
                        document.title = response.data.name;

                        setPageTitle(response.data.name);
                    }
                } catch (error) {
                    // Silently fail - don't break navigation if product fetch fails
                    console.error('Error fetching product name for title:', error);
                }
            };

            return pageTitle;
        },
        headerRender: (props: any, defaultDom: ReactNode) => {
            return (
                <>
                    <NoticeBar />
                    {defaultDom}
                </>
            );
        },
        unAccessible: <NoAccess />,
        childrenRender: (children: ReactNode, x: any) => {
            useEffect(() => {
                Logger.log_verbose(`Page visited: ${window.location.pathname}`);
            }, [window.location.pathname]);

            useAggressiveClickDetector();

            const ResultPage: React.FC<{ id: string }> = ({ id }) => {
                return (
                    <Result
                        status="warning"
                        title={<FormattedMessage id={id} />}
                        extra={
                            <Button type="primary" size="large" onClick={() => window.location.reload()}>
                                <FormattedMessage id="common.Reload page" />
                            </Button>
                        }
                    />
                );
            };
            try {
                if (navigator.onLine) {
                    return (
                        <ErrorBoundary>
                            <EndingPoliciesNotifier />
                            {children}
                        </ErrorBoundary>
                    );
                }
                return <ResultPage id="common.No internet connection!" />;
            } catch (e) {
                console.log(e);
                return <ResultPage id="common.warning.notice" />;
            }
        },
        ...initialState?.settings,
    };
};
